import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

export type AcademicMajor = Readonly<{
  id: number;
  name: string;
  category?: string;
}>;

export type UserAcademicHistory = Readonly<{
  id: number;
  user_id: number;
  institution_id?: number | null;
  institution_name?: string;
  institution_city?: string;
  institution_state?: string;
  did_graduate?: boolean;
  degree_id?: number;
  graduation_date?: Date;
  gpa?: number;
  major_gpa?: number;
  awards_accomplishments?: string;
  created_at: string;
  updated_at: string;
  majors?: AcademicMajor[];
  majorIds?: number[];
}>;

export type UserAcademicHistoryRequest = Omit<
  UserAcademicHistory,
  'id' | 'user_id' | 'created_at' | 'updated_at'
>;

export const userAcademicHistoryApi = createApi({
  reducerPath: 'userAcademicHistoryApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getUserAcademicHistory: builder.query<UserAcademicHistory[], undefined>({
      query: () => ({
        url: '/user-academic-history',
        method: 'GET',
      }),
    }),

    getAcademicHistoryById: builder.query<UserAcademicHistory, number>({
      query: (id) => ({
        url: `/user-academic-history/${id}`,
        method: 'GET',
      }),
    }),

    createAcademicHistory: builder.mutation<UserAcademicHistory, UserAcademicHistoryRequest>({
      query: (academicHistoryData) => {
        const { majorIds, ...data } = academicHistoryData;

        return {
          url: '/user-academic-history',
          method: 'POST',
          body: {
            ...data,
            ...(majorIds && majorIds.length > 0 ? { majorIds } : {}),
          },
        };
      },
    }),

    updateAcademicHistory: builder.mutation<
      UserAcademicHistory,
      { id: number; data: UserAcademicHistoryRequest }
    >({
      query: ({ id, data }) => {
        const { majorIds, ...updateData } = data;

        return {
          url: `/user-academic-history/${id}`,
          method: 'PUT',
          body: {
            ...updateData,
            ...(majorIds && majorIds.length > 0 ? { majorIds } : {}),
          },
        };
      },
    }),

    deleteAcademicHistory: builder.mutation<undefined, number>({
      query: (id) => ({
        url: `/user-academic-history/${id}`,
        method: 'DELETE',
      }),
    }),

    deleteAllAcademicHistory: builder.mutation<{ message: string }, undefined>({
      query: () => ({
        url: '/user-academic-history',
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetUserAcademicHistoryQuery,
  useGetAcademicHistoryByIdQuery,
  useCreateAcademicHistoryMutation,
  useUpdateAcademicHistoryMutation,
  useDeleteAcademicHistoryMutation,
  useDeleteAllAcademicHistoryMutation,
} = userAcademicHistoryApi;
